/** El mismo bucle que abre la portada de la tienda, ya en el repositorio. */
const VIDEO_SRC = "/assets/video/hero-loop.mp4";

/** Su primer fotograma: póster del vídeo Y la imagen fija sin movimiento. */
const POSTER_SRC = "/assets/video/hero-loop-poster.webp";

export interface CircleVideoProps {
  /** Lado del círculo, en píxeles. */
  size?: number;
  /** `false` = la persona pidió movimiento reducido. */
  motionOk: boolean;
}

/**
 * El vídeo circular de marca: el "globo" que acompaña al desplazamiento.
 *
 * En el sistema actual esto sustituyó a la Tierra que traía el componente
 * original — es el bucle de burbujas violeta de la landing, recortado en
 * círculo, con el halo que lo despega del fondo.
 *
 * ── El archivo ya estaba aquí ────────────────────────────────────────
 * `anim-loop.mp4` de producción y `hero-loop.mp4` de este repositorio son
 * el MISMO archivo (mismo sha256): el que ya usa la portada de la tienda.
 * Por eso se apunta al que existe en `public/` y no se copia otro 1.2 MB
 * con distinto nombre.
 *
 * ── Con movimiento reducido hay imagen, no vídeo ─────────────────────
 * Y no un `<video>` en pausa: un `<img>`. La diferencia es lo que NO
 * ocurre — el navegador no descarga el MP4, no descodifica nada y no
 * queda un elemento multimedia al que un gesto accidental pueda darle al
 * play. Es el mismo criterio que ya aplica `ShopHeroVideo`, y el póster
 * es el fotograma 0, así que la imagen fija y el vídeo enseñan lo mismo.
 *
 * Es decoración: `aria-hidden` y fuera del orden de tabulación. No dice
 * nada que el texto de al lado no diga.
 */
export function CircleVideo({ size = 300, motionOk }: CircleVideoProps) {
  return (
    <div
      className="relative overflow-hidden rounded-full"
      style={{
        width: size,
        height: size,
        boxShadow:
          "0 0 60px rgba(92,78,213,0.35), 0 0 20px rgba(92,78,213,0.25), inset -18px -6px 40px rgba(255,255,255,0.18), inset 16px 4px 30px rgba(0,0,0,0.28)",
      }}
    >
      {motionOk ? (
        <video
          className="h-full w-full object-cover"
          poster={POSTER_SRC}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          disablePictureInPicture
          aria-hidden="true"
          tabIndex={-1}
        >
          <source src={VIDEO_SRC} type="video/mp4" />
        </video>
      ) : (
        <img
          src={POSTER_SRC}
          alt=""
          aria-hidden="true"
          decoding="async"
          className="h-full w-full object-cover"
        />
      )}
    </div>
  );
}
